import ClanRankPanel from "../components/clan/ClanRankPanel";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Shield, UserPlus, Swords, LogOut, Trash2, Sparkles, Power, Trophy, UserCheck, Crown, Image as ImageIcon, Check, Users, Gamepad2, Monitor, Edit3, X, Clock } from "lucide-react";
import { FaPlaystation, FaXbox, FaDesktop } from "react-icons/fa6";
import { toast } from "sonner";
import { challengeReadiness } from "../components/clan/challengeReadiness";
import ChallengeModal from "../components/clan/ChallengeModal";
import InviteModal from "../components/clan/InviteModal";
import MembersList from "../components/clan/MembersList";
import ClanLogo from "../components/clan/ClanLogo";

function formatSuspensionRemaining(seconds) {
  const total = Math.max(0, Number(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h <= 0) return `${Math.max(1, m)} دقيقة`;
  if (m <= 0) return `${h} ساعة`;
  return `${h} ساعة و${m} دقيقة`;
}

// دالة مساعدة لعرض شارة المنصة
function renderPlatformBadge(platform) {
  if (!platform) return null;
  const p = String(platform).toLowerCase();

  if (p.includes("ps") || p.includes("playstation")) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#003791]/30 border border-[#003791]/50 text-[#0070d1]" title="PlayStation">
        <FaPlaystation size={10} /> PS5
      </span>
    );
  }
  if (p.includes("xbox")) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#107c41]/30 border border-[#107c41]/50 text-[#107c41]" title="Xbox">
        <FaXbox size={10} /> Xbox
      </span>
    );
  }
  if (p.includes("pc") || p.includes("computer") || p.includes("steam")) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-500/10 border border-purple-500/30 text-purple-300" title="PC">
        <FaDesktop size={10} /> PC
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-white/5 border border-white/10 text-white/70">
      {platform.toUpperCase()}
    </span>
  );
}

export function ClanHeader({ clan, rankSystem, isStaff, onOpenEdit }) {
  if (!clan) return null;

  const isPlusClan = clan.max_members >= 9 || clan.isPlusClan || clan.is_plus;
  const remainingToReward = 6 - (clan.member_ids?.length || 0);


  return (
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-gold-500/10 via-transparent to-transparent" />
      
      {(clan?.logo_url || clan?.logo) && (
        <>
          <img
            src={clan?.logo_url || clan?.logo}
            alt={clan?.name}
            className="absolute inset-0 w-full h-full object-cover blur-md opacity-25 scale-110 pointer-events-none select-none"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#111113]/70 to-[#111113] pointer-events-none" />
        </>
      )}

      <div className="relative text-center space-y-3">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-gold-500 bg-gold-500/10 border border-gold-500/20 px-3 py-1 rounded-full">
            <ClanLogo
              clan={clan}
              className="h-4 w-4 rounded-sm overflow-hidden bg-gold-500/20 grid place-items-center"
              fallbackIconSize={12}
              fallbackIconClassName="text-gold-500"
            />
            <span>[{clan.tag}]</span>
            {isPlusClan && (
              <span className="inline-flex items-center gap-1 font-sans font-bold text-gold-400">
                <Sparkles size={11} className="text-[#d4af37]" /> Plus
              </span>
            )}
          </div>


          {isStaff && (
            <button
              onClick={onOpenEdit}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white text-xs font-bold transition"
            >
              <Edit3 size={12} /> تعديل بيانات الكلان
            </button>
          )}
        </div>

        <h1 className="font-display font-black text-3xl md:text-5xl text-white tracking-wide leading-tight drop-shadow-md">
          {clan.name}
        </h1>

        <p className="text-white/60 max-w-2xl mx-auto text-xs md:text-sm leading-relaxed">
          {clan.description || "— لا يوجد وصف للكلان —"}
        </p>

        <ClanRankPanel clan={clan} rankSystem={rankSystem}/>

        {/* إحصائيات الكلان */}
        <div className="mt-6 grid grid-cols-3 gap-3 md:gap-4 max-w-lg mx-auto">
          <Stat label="النقاط" value={clan.points || 0} highlight />
          <Stat label="انتصارات" value={clan.wins || 0} />
          <Stat label="هزائم" value={clan.losses || 0} />
        </div>

        <div className="mt-3 text-xs text-white/40 flex items-center justify-center gap-1.5 font-bold">
          <Users size={14} /> {clan.member_ids?.length || 0} / {clan.max_members || 6} لاعب
        </div>

        {!clan.founder_reward_given && remainingToReward > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-gold-500/10 border border-gold-500/20 text-xs text-gold-400 font-bold shadow-sm">
            <Sparkles size={13} className="text-gold-500 animate-pulse" />
            <span>اكمل {remainingToReward} لاعبين لتفعيل شارة Plus مجاناً لمدة 7 أيام!</span>
          </div>
        )}

        {clan.suspension_active && (
          <div className="mt-4 rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-2.5 text-xs text-destructive text-right max-w-2xl mx-auto">
            <div className="font-bold">الكلان موقوف مؤقتاً عن التسجيل في البطولات والدوريات</div>
            <div className="text-white/70 mt-0.5">
              المتبقي: {formatSuspensionRemaining(clan.suspension_remaining_seconds)}
              {clan.suspension_reason ? ` • السبب: ${clan.suspension_reason}` : ""}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-3 text-center transition hover:border-white/20">
      <div className={`text-xl md:text-2xl font-display font-black ${highlight ? "text-gold-500" : "text-white"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{label}</div>
    </div>
  );
}

function ActionBar({ canJoin, isFull, isStaff, isMember, isLeader, canSearchChallenge, challengeWarning, clanId, onJoin, onChallenge, onInvite, onLeave, onDelete, onArchive, leaving = false }) {
  return (
    <div className="flex gap-2 flex-wrap items-center">
      {canJoin && !isFull && (
        <button
          data-testid="request-join-btn"
          onClick={onJoin}
          className="px-4 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 shadow-[0_0_15px_rgba(255,204,0,0.3)] hover:scale-105 transition-all text-xs flex items-center gap-1.5"
        >
          <UserPlus size={14} /> طلب انضمام
        </button>
      )}
      {canJoin && isFull && (
        <div className="px-4 py-2 rounded-xl bg-destructive/10 text-destructive text-xs font-bold border border-destructive/20">الكلان ممتلئ</div>
      )}
      {isStaff && (
        <>
          <button
            data-testid="challenge-btn"
            onClick={onChallenge}
            disabled={false}
            className="px-4 py-2.5 rounded-xl bg-destructive text-white font-black hover:bg-destructive/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-xs shadow-md transition"
          >
            <Swords size={14} /> البحث عن تحدي
          </button>
          {!isFull && (
            <button
              data-testid="invite-btn"
              onClick={onInvite}
              className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 flex items-center gap-1.5 text-xs text-white font-bold transition"
            >
              <UserPlus size={14} /> دعوة لاعب
            </button>
          )}
        </>
      )}
      {isMember && !isLeader && (
        <button
          onClick={onLeave}
          disabled={leaving}
          data-testid="leave-clan-btn"
          className="px-4 py-2.5 rounded-xl border border-red-500/40 bg-red-600/10 text-red-400 hover:bg-red-600/20 active:scale-95 transition-all disabled:opacity-60 flex items-center gap-1.5 text-xs font-bold"
        >
          <LogOut size={14} className={leaving ? "animate-pulse" : ""} /> {leaving ? "جاري المغادرة..." : "مغادرة الكلان"}
        </button>
      )}
      {isLeader && (
        <>
          <button
            data-testid="archive-clan-btn"
            onClick={onArchive}
            className="px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white/60 hover:bg-white/10 flex items-center gap-1.5 text-xs transition font-bold"
          >
            <Power size={14} /> أرشفة الكلان
          </button>
          <button
            onClick={onDelete}
            className="px-4 py-2.5 rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 flex items-center gap-1.5 text-xs transition font-bold"
          >
            <Trash2 size={14} /> حذف الكلان
          </button>
        </>
      )}
      {isStaff && !canSearchChallenge && (
        <div className="w-full text-xs text-amber-400/80 mt-1 font-bold">
          ⚠️ {challengeWarning}
        </div>
      )}
    </div>
  );
}

function AttendancePanel({ clan, attendance, canInteract, canManage, isCheckedIn, onCheckIn, onCheckOut, onRemove, highlightCheckIn }) {
  const summary = attendance?.summary || clan?.attendance || {};
  const checked = attendance?.checked_in || [];
  const count = summary.count || 0;
  const required = summary.required || 6;
  const green = !!summary.is_green;

  return (
    <section data-testid="attendance-panel" className="bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
      <div className={`px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap ${green ? "bg-emerald-500/10" : "bg-white/[0.02]"}`}>
        <h2 className="font-display font-black text-lg text-white flex items-center gap-2">
          <UserCheck size={18} className={green ? "text-emerald-400" : "text-white/40"} />
          تحضير الكلان
        </h2>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${
          green ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-white/5 text-white/60 border border-white/10"
        }`}>
          <span className={`h-2 w-2 rounded-full flex-shrink-0 ${green ? "bg-emerald-400" : "bg-white/30"}`} />
          {count} / {required}
        </div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-white/40">يمكن لأعضاء الكلان فقط التحضير لتفعيل الجاهزية للمباريات.</p>

        {canInteract ? (
          isCheckedIn ? (
            <button
              onClick={onCheckOut}
              className={`px-5 py-2.5 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-bold text-white transition ${highlightCheckIn ? "ring-2 ring-emerald-400/60 shadow-[0_0_18px_rgba(16,185,129,0.45)]" : ""}`}
            >
              إلغاء التحضير
            </button>
          ) : (
            <button
              onClick={onCheckIn}
              className={`px-5 py-2.5 rounded-xl bg-emerald-500 text-black font-black hover:bg-emerald-400 text-xs transition shadow-[0_0_15px_rgba(16,185,129,0.3)] ${highlightCheckIn ? "animate-pulse" : ""}`}
            >
              تحضير الآن 🟢
            </button>
          )
        ) : (
          <div className="text-xs text-white/30 italic">التفاعل متاح لأعضاء الكلان فقط.</div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {checked.length === 0 ? (
            <div className="col-span-full text-xs text-white/30 py-2">لا يوجد محضرون حالياً.</div>
          ) : checked.map((p, index) => (
            <div key={p.id} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-bold text-white truncate text-xs"><span className="text-emerald-400 ml-1">{p.attendance_position || index + 1}.</span>{p.username}</div>
                  <div className="text-[10px] text-white/40 truncate">{p.act || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {renderPlatformBadge(p.gaming_platform || p.platform)}
                {canManage && (
                  <button onClick={() => onRemove(p.id)} className="text-[10px] px-2 py-1 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10">إزالة</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TopMvpWidget({ rows }) {
  const rankStyle = [
    { bg: "bg-gold-500/10 border-gold-500/30", num: "text-gold-500" },
    { bg: "bg-white/5 border-white/10", num: "text-white/50" },
    { bg: "bg-amber-700/10 border-amber-700/20", num: "text-amber-600" },
  ];

  return (
    <section data-testid="top-mvp-widget" className="bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2 bg-gold-500/5">
        <Crown size={18} className="text-gold-500" />
        <h2 className="font-display font-black text-lg text-white">أفضل لاعبي الكلان (Top MVP)</h2>
        {rows?.length > 0 && (
          <span className="mr-auto text-xs text-white/40 font-mono">{rows.length} لاعب</span>
        )}
      </div>

      <div className="p-5">
        {rows?.length ? (
          <div className="space-y-2">
            {rows.map((r, idx) => {
              const s = rankStyle[idx] || { bg: "bg-black/30 border-white/5", num: "text-white/30" };
              return (
                <Link
                  key={r.id}
                  to={`/players/${r.user_id || r.id}`}
                  className={`rounded-xl border px-4 py-2.5 flex items-center gap-3 transition-all hover:border-gold-500/50 hover:bg-gold-500/10 ${s.bg}`}
                >
                  <div className={`text-sm font-display font-black w-5 text-center ${s.num}`}>#{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-xs md:text-sm text-white truncate flex items-center gap-2">
                      <span>{r.username}</span>
                      {renderPlatformBadge(r.gaming_platform || r.platform)}
                    </div>
                    <div className="text-[10px] text-white/45 mt-0.5">
                      <span className="text-gold-500 font-bold">★ {r.mvp_count} MVP</span>
                      <span className="mx-1 text-white/20">•</span>
                      <span>{r.attendances || 0} حضور</span>
                    </div>
                  </div>
                  {idx === 0 && <Crown size={14} className="text-gold-500 flex-shrink-0" />}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="py-6 text-center text-xs text-white/30">
            <Crown size={24} className="mx-auto mb-2 text-white/15" />
            لا توجد إحصائيات MVP بعد
          </div>
        )}
      </div>
    </section>
  );
}

function TrophyRoom({ trophies }) {
  return (
    <section data-testid="trophy-room" className="bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-2 bg-gold-500/5">
        <div className="flex items-center gap-2">
          <Trophy size={18} className="text-gold-500" />
          <h2 className="font-display font-black text-lg text-white">خزينة الكؤوس والإنجازات</h2>
        </div>
        <span className="text-xs text-gold-500 font-mono font-bold">{trophies?.length || 0} كأس</span>
      </div>

      {!trophies || trophies.length === 0 ? (
        <div className="p-8 text-center text-xs text-white/30">
          <Trophy size={32} className="mx-auto mb-2 text-white/15" />
          لم يحقق الكلان ألقاباً بعد. استعد للمنافسة!
        </div>
      ) : (
        <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {trophies.map((t) => (
            <div
              key={t.id}
              data-testid={`trophy-${t.id}`}
              className="bg-gold-500/5 border border-gold-500/20 rounded-xl p-3.5 flex items-center gap-3"
            >
              <div className="h-10 w-10 rounded-xl bg-gold-500/15 border border-gold-500/30 grid place-items-center text-gold-500 flex-shrink-0">
                <Trophy size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-xs text-gold-400 truncate">{t.label}</div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  {t.kind === "league" ? "بطولة شهرية" : "بطولة رسمية"} • {new Date(t.awarded_at).toLocaleDateString("ar")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function LeaguesJoin({ clanLeagueIds, leagues, reload, clanMembersCount }) {
  const canParticipate = (clanMembersCount || 0) >= 6;
  const join = async (lid) => {
    if (!canParticipate) {
      return toast.error("لا يمكنك المشاركة إلا بعد وصول أعضاء الكلان إلى 6 لاعبين.");
    }
    try {
      await api.post(`/leagues/${lid}/join`);
      toast.success("تم التسجيل في الدوري");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذّر التسجيل");
    }
  };
  if (!leagues || leagues.length === 0) return null;
  return (
    <section data-testid="leagues-join" className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
      <h2 className="font-display font-black text-lg text-white mb-3 flex items-center gap-2">
        <Trophy size={18} className="text-gold-500" /> الدوريات المتاحة للتسجيل
      </h2>
      {!canParticipate && (
        <div className="mb-3 text-xs text-destructive font-bold">
          ⚠️ يتطلب المشاركة وجود 6 لاعبين على الأقل في الكلان.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {leagues.map((lg) => {
          const isJoined = clanLeagueIds.includes(lg.id);
          return (
            <div key={lg.id} className="bg-black/30 border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid={`league-${lg.id}`}>
              <Trophy size={16} className="text-gold-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-xs text-white truncate">{lg.name}</div>
                <div className="text-[10px] text-white/40">{lg.game} • {lg.is_custom ? "مخصص" : "رسمي"}</div>
              </div>
              {isJoined ? (
                <span className="text-[10px] uppercase font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-1 rounded-lg">مسجّل</span>
              ) : (
                <button
                  data-testid={`join-league-${lg.id}`}
                  onClick={() => join(lg.id)}
                  disabled={!canParticipate}
                  className="px-3 py-1.5 rounded-lg text-xs bg-gold-500 text-black font-black hover:bg-gold-400 disabled:opacity-50"
                >
                  سجّل الكلان
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RequestsList({ requests, onAccept, onReject }) {
  const incomingRequests = requests.filter(r => 
    r.type !== "invite" && r.kind !== "clan_invite" && r.sender_type !== "clan" && r.direction !== "outgoing"
  );

  return (
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
      <h2 className="font-display font-black text-lg text-white mb-3">طلبات الانضمام ({incomingRequests.length})</h2>
      {incomingRequests.length === 0 ? (
        <div className="text-white/30 text-xs">لا توجد طلبات انضمام جديدة</div>
      ) : (
        <div className="space-y-2">
          {incomingRequests.map((r) => (
            <div key={r.id} className="bg-black/30 border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={`request-${r.id}`}>
              <div className="h-8 w-8 rounded-lg bg-gold-500/10 text-gold-500 font-bold grid place-items-center text-xs">
                {r.username[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-xs text-white truncate flex items-center gap-2">
                  <span>{r.username}</span>
                  {renderPlatformBadge(r.gaming_platform || r.platform)}
                </div>
                <div className="text-[10px] text-white/40">طلب انضمام</div>
              </div>
              <div className="flex items-center gap-1.5">
                <button data-testid={`accept-${r.id}`} onClick={() => onAccept(r.id)} className="px-3 py-1 rounded-lg bg-gold-500 text-black text-xs font-bold hover:bg-gold-400">قبول</button>
                <button data-testid={`reject-${r.id}`} onClick={() => onReject(r.id)} className="px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70 hover:bg-white/10">رفض</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ChallengesList({ challenges, currentClanId, onAccept, onReject }) {
  if (!challenges || challenges.length === 0) return null;
  return (
    <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl">
      <h2 className="font-display font-black text-lg text-white mb-3">التحديات والمواجهات ({challenges.length})</h2>
      <div className="space-y-2">
        {challenges.map((c) => {
          const incoming = c.opponent_clan_id === currentClanId;
          return (
            <div key={c.id} className="bg-black/30 border border-white/5 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid={`challenge-${c.id}`}>
              <div className="h-8 w-8 rounded-lg bg-destructive/10 text-destructive grid place-items-center">
                <Swords size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-xs text-white">
                  {incoming ? `تحدٍ وارد من ${c.challenger_name}` : `بانتظار رد ${c.opponent_name}`}
                </div>
                <div className="text-[10px] text-white/40">[{incoming ? c.challenger_tag : c.opponent_tag}] • BO3 Call of Duty</div>
              </div>
              {incoming ? (
                <div className="flex gap-1.5">
                  <button data-testid={`ch-accept-${c.id}`} onClick={() => onAccept(c.id)} className="px-3 py-1 rounded-lg bg-gold-500 text-black text-xs font-bold hover:bg-gold-400">قبول التحدي</button>
                  <button data-testid={`ch-reject-${c.id}`} onClick={() => onReject(c.id)} className="px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70">رفض</button>
                </div>
              ) : (
                <span className="text-xs text-white/40 font-bold">قيد الانتظار...</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function ClanDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { user, refresh } = useAuth();
  const [clan, setClan] = useState(null);
  const [rankSystem, setRankSystem] = useState({ ranks: [], settings: {} });
  const [requests, setRequests] = useState([]);
  const [allClans, setAllClans] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [myClan, setMyClan] = useState(null);
  const [myClanAttendance, setMyClanAttendance] = useState(null);
  const [mvpRows, setMvpRows] = useState([]);
  const [showChallenge, setShowChallenge] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const inviteRequest = useRef(null);
  const inviteTimer = useRef(null);
  const inviteVersion = useRef(0);
  useEffect(() => () => {
    inviteVersion.current += 1;
    inviteRequest.current?.abort();
    clearTimeout(inviteTimer.current);
  }, []);
  const [opponent, setOpponent] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // 📝 حالة نافذة تعديل بيانات الكلان
  const [showEditClanModal, setShowEditClanModal] = useState(false);
  const [editClanForm, setEditClanForm] = useState({ name: "", tag: "", description: "" });
  const [editClanBusy, setEditClanBusy] = useState(false);

  const handleErr = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clans/${id}`);
      setClan(data);
      setEditClanForm({ name: data.name || "", tag: data.tag || "", description: data.description || "" });
      api.get(`/clans/${id}/attendance`).then((r) => setAttendance(r.data)).catch(() => setAttendance(null));
      api.get(`/clans/${id}/mvp-leaderboard`).then((r) => setMvpRows(r.data || [])).catch(() => setMvpRows([]));
      const isStaff = user && (
        user.role === "admin" ||
        user.id === data.leader_id ||
        data.vice_leader_ids?.includes(user.id)
      );
      if (isStaff) {
        try {
          const r = await api.get(`/clans/${id}/requests`);
          setRequests(r.data);
        } catch {}
        try {
          const ch = await api.get(`/clans/${id}/challenges`);
          setChallenges(ch.data);
        } catch {}
      }
    } catch (err) {
      handleErr(err);
    }
  }, [id, user]);

  useEffect(() => {
    load();
    api.get("/online-clans").then((r) => setAllClans(r.data)).catch(() => {});
    api.get("/rank-system").then((r) => setRankSystem(r.data || {ranks:[],settings:{}})).catch(() => {});
    api.get("/leagues/active").then((r) => setLeagues(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!user?.clan_id) {
      setMyClan(null);
      setMyClanAttendance(null);
      return;
    }
    api.get(`/clans/${user.clan_id}`).then((r) => setMyClan(r.data)).catch(() => setMyClan(null));
    api.get(`/clans/${user.clan_id}/attendance`).then((r) => setMyClanAttendance(r.data)).catch(() => setMyClanAttendance(null));
  }, [user?.clan_id, clan?.id]);

  
  const flags = useMemo(() => {
    if (!clan) return {};
    const isLeader = user?.id === clan.leader_id;
    const isViceLeader = clan.vice_leader_ids?.includes(user?.id);
    const isStaff = isLeader || isViceLeader || user?.role === "admin";
    const isMember = clan.member_ids?.includes(user?.id);
    const canJoin = !!user && !user.clan_id && !isMember;
    const isFull = (clan.member_ids?.length || 0) >= (clan.max_members || 6);
    return { isLeader, isViceLeader, isStaff, isMember, canJoin, isFull };
  }, [clan, user]);

  
  const availableOpponents = useMemo(
    () => allClans.filter((c) => clan && c.id !== clan.id),
    [allClans, clan]
  );

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      api.get(`/clans/${id}`, {timeout: 5000}).then(r => setClan(r.data)).catch(() => {});
      api.get(`/clans/${id}/attendance`, {timeout: 5000}).then(r => setAttendance(r.data)).catch(() => {});
      api.get("/rank-system", {timeout: 5000}).then(r => setRankSystem(r.data)).catch(() => {});
      api.get("/online-clans", {timeout: 5000}).then(r => setAllClans(r.data)).catch(() => {});
      if (user?.clan_id) api.get(`/clans/${user.clan_id}/attendance`, {timeout: 5000}).then(r => setMyClanAttendance(r.data)).catch(() => {});
    };
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [id, user?.clan_id]);

  const ownChallengeClan = clan?.id === user?.clan_id ? clan : myClan;
  const ownChallengeAttendance = clan?.id === user?.clan_id ? attendance : myClanAttendance;
  const ownChallenge = challengeReadiness(ownChallengeClan, ownChallengeAttendance, user);
  const ownAttendanceReady = ownChallenge.canSend;
  const responseChallenge = challengeReadiness(clan, attendance, user);
  const isExternalLeader = !!(user && clan && user.clan_id && user.clan_id !== clan.id && myClan && (myClan.leader_id === user.id || myClan.vice_leader_ids?.includes(user.id)));
  const targetIsGreen = Number(attendance?.summary?.count ?? clan?.attendance?.count ?? 0) >= 6;
  const canInstantChallenge = isExternalLeader && ownAttendanceReady && targetIsGreen;
  const isCheckedIn = !!attendance?.checked_in?.some((p) => p.id === user?.id);
  const highlightCheckIn = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("highlight_checkin") === "1";
  }, [location.search]);

  if (!clan) return <div className="text-white/40 text-center py-12">جارٍ التحميل...</div>;

  const join = async () => {
    try { await api.post(`/clans/${id}/join-request`); toast.success("تم إرسال طلب الانضمام"); }
    catch (err) { handleErr(err); }
  };

  const accept = async (rid) => {
    try {
      const { data } = await api.post(`/clans/${id}/requests/${rid}`, { action: "accept" });
      if (data?.reward_granted) {
        toast.success("🎁 مبروك! امتلأ الكلان وتم تفعيل Plus مجاناً!", { duration: 6000 });
        await refresh();
      } else {
        toast.success("تم القبول");
      }
      load();
    } catch (err) { handleErr(err); }
  };

  const reject = async (rid) => {
    try {
      const { data } = await api.post(`/clans/${id}/requests/${rid}`, { action: "reject" });
      toast.success("تم الرفض");
      load();
    } catch (err) { handleErr(err); }
  };

  const kick = async (mid) => {
    if (!confirm("طرد هذا اللاعب من الكلان؟")) return;
    await api.post(`/clans/${id}/kick/${mid}`);
    toast.success("تم الطرد");
    load();
  };

  const promote = async (mid) => {
    try { await api.post(`/clans/${id}/promote/${mid}`); load(); }
    catch (err) { handleErr(err); }
  };

  const removeAttendance = async (memberId) => {
    if (!confirm("إزالة تحضير هذا اللاعب؟")) return;
    try {
      await api.post(`/clans/${id}/attendance/remove/${memberId}`);
      toast.success("تمت إزالة التحضير");
      load();
    } catch (err) { handleErr(err); }
  };

  const leave = async () => {
    if (!confirm("هل أنت تأكد من مغادرة الكلان؟")) return;
    setLeaving(true);
    try {
      const { data } = await api.post(`/clans/${id}/leave`);
      await refresh();
      const cdHours = Number(data?.cooldown_hours || 3);
      toast.success(`غادرت الكلان. يمكنك الانضمام مجددًا بعد ${cdHours} ساعات`);
      load();
    } catch (err) {
      handleErr(err);
    } finally {
      setLeaving(false);
    }
  };

  const delClan = async () => {
 
    if (!confirm("حذف الكلان نهائياً؟")) return;
    await api.delete(`/clans/${id}`);
    await refresh();
    window.location.href = "/clans";
  };

  const archive = async () => {

    if (!confirm("أرشفة الكلان؟ سيتم طرد جميع الأعضاء وإيقاف الكلان.")) return;
    try {
      await api.post(`/clans/${id}/archive`);
      toast.success("تم إيقاف الكلان وطرد الأعضاء");
      await refresh();
      window.location.href = "/clans";
    } catch (err) { handleErr(err); }
  };

  // 📝 حفظ تعديلات بيانات الكلان (الاسم، التاخت، الوصف)
  const saveClanDetails = async (e) => {
    e.preventDefault();
    setEditClanBusy(true);
    try {
      await api.put(`/admin/clans/${id}`, editClanForm);
      toast.success("تم تحديث بيانات الكلان بنجاح! 🚀");
      setShowEditClanModal(false);
      load();
    } catch (err) {
      handleErr(err);
    } finally {
      setEditClanBusy(false);
    }
  };

  const challenge = async (e) => {
    e.preventDefault();
    if (!opponent) return toast.error("اختر خصماً");
    if (!user?.clan_id) return toast.error("يجب أن تكون في كلان لإرسال تحدٍ");
    if (!ownAttendanceReady) return toast.error(ownChallenge.reason);
    try {
      await api.post(`/clans/${user.clan_id}/challenge`, { opponent_clan_id: opponent });
      toast.success("تم إرسال التحدي");
      setShowChallenge(false);
      setOpponent("");
      load();
    } catch (err) { handleErr(err); }
  };

  const instantChallenge = async () => {
    if (!canInstantChallenge) return;
    try {
      await api.post(`/clans/${id}/instant-challenge`);
      toast.success("تم إرسال التحدي المباشر بنجاح");
      load();
    } catch (err) { handleErr(err); }
  };

  const checkIn = async () => {
    try {
      const { data } = await api.post(`/clans/${id}/attendance/checkin`);
      setAttendance(data);
      if (user?.clan_id) {
        api.get(`/clans/${user.clan_id}/attendance`).then((r) => setMyClanAttendance(r.data)).catch(() => {});
      }
      toast.success("تم تسجيل تحضيرك");
    } catch (err) { handleErr(err); }
  };

  const checkOut = async () => {
    try {
      const { data } = await api.post(`/clans/${id}/attendance/checkout`);
      setAttendance(data);
      if (user?.clan_id) {
        api.get(`/clans/${user.clan_id}/attendance`).then((r) => setMyClanAttendance(r.data)).catch(() => {});
      }
      toast.success("تم إلغاء التحضير");
    } catch (err) { handleErr(err); }
  };

  const respondChallenge = async (chId, action) => {
    if (!responseChallenge.canManage) return toast.error(responseChallenge.operatorReason);
    if (action === "accept" && !responseChallenge.canSend) return toast.error(responseChallenge.reason);
    try {
      const { data } = await api.post(`/challenges/${chId}`, { action });
      if (action === "accept" && data?.match?.id) {
        toast.success("تم قبول التحدي. بدأت المباراة!");
        window.location.href = `/matches/${data.match.id}`;
      } else {
        toast.success(action === "accept" ? "تم القبول" : "تم رفض التحدي");
        load();
      }
    } catch (err) { handleErr(err); }
  };

  const searchInvite = (val) => {
    setInviteSearch(val);
    setInviteResults([]);
    clearTimeout(inviteTimer.current);
    inviteRequest.current?.abort();
    const version = ++inviteVersion.current;
    const query = val.trim();
    if (!query) return;
    inviteTimer.current = setTimeout(async () => {
      const controller = new AbortController();
      inviteRequest.current = controller;
      try {
        const { data } = await api.get("/users/search", {
          params: { q: query, tab: "free", limit: 30 }, signal: controller.signal, timeout: 10000
        });
        if (controller.signal.aborted || version !== inviteVersion.current) return;
        const rows = Array.isArray(data) ? data : data.items || [];
        setInviteResults(rows.filter(u => !u.clan_id && u.id !== user?.id));
      } catch (error) {
        if (!controller.signal.aborted && version === inviteVersion.current)
          toast.error("تعذر البحث عن اللاعبين. حاول مجددًا.");
      }
    }, 300);
  };

  const sendInvite = async (uid) => {
    try {
      await api.post(`/clans/${id}/invite`, { user_id: uid });
      toast.success("تم إرسال الدعوة");
      setInviteResults(current => current.filter(u => u.id !== uid));
    } catch (err) { handleErr(err); }
  };

  const onUploadClanLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !clan) return;

    const isPlusClan = !!(clan.isPlusClan || clan.is_plus || (clan.max_members || 0) >= 9);
    const lowerName = (f.name || "").toLowerCase();
    const isGif = (f.type || "").toLowerCase() === "image/gif" || lowerName.endsWith(".gif");
    const allowed = ["image/gif", "image/png", "image/jpeg", "image/jpg"];
    if (!allowed.includes((f.type || "").toLowerCase())) {
      return toast.error("صيغة الشعار غير مدعومة. المسموح: GIF, PNG, JPG");
    }
    if (isGif && !isPlusClan) {
      return toast.error("عذراً، ميزة الشعار المتحرك GIF حصرية لكلانات البلس!");
    }

    setLogoUploading(true);
    try {
      let data;
      const primary = `/clans/${id}/logo/upload`;
      const legacy = `/clans/upload-logo`;
      const multipartHeaders = { "Content-Type": "multipart/form-data" };

      const primaryMethods = [api.post, api.put];
      let primaryErr = null;
      for (const sender of primaryMethods) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const res = await sender(primary, fd, { headers: multipartHeaders });
          data = res.data;
          primaryErr = null;
          break;
        } catch (err) {
          primaryErr = err;
          const status = err?.response?.status;
          if (status !== 404 && status !== 405) throw err;
        }
      }

      if (!data) {
        const legacyMethods = [api.post, api.put];
        let legacyErr = primaryErr;
        for (const sender of legacyMethods) {
          try {
            const fdLegacy = new FormData();
            fdLegacy.append("file", f);
            fdLegacy.append("clan_id", id);
            const resLegacy = await sender(legacy, fdLegacy, { headers: multipartHeaders });
            data = resLegacy.data;
            legacyErr = null;
            break;
          } catch (err) {
            legacyErr = err;
            const status = err?.response?.status;
            if (status !== 404 && status !== 405) throw err;
          }
        }
        if (!data && legacyErr) throw legacyErr;
      }

      if (data?.url) {
        setClan((prev) => (prev ? { ...prev, logo: data.url } : prev));
      }
      toast.success("تم تحديث شعار الكلان");
      load();
    } catch (err) {
      handleErr(err);
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* ── Hero header ── */}
      <ClanHeader clan={clan} rankSystem={rankSystem} isStaff={flags.isStaff} onOpenEdit={() => setShowEditClanModal(true)} />

      {/* ── Action buttons ── */}
      <div className="px-1">
        <ActionBar
          canJoin={flags.canJoin}
          isFull={flags.isFull}
          isStaff={flags.isStaff}
          isMember={flags.isMember}
          isLeader={flags.isLeader}
          canSearchChallenge={ownAttendanceReady}
          challengeWarning={ownChallenge.reason}
 
          clanId={id}
          onJoin={join}
          onChallenge={() => { setShowChallenge(true); api.get("/online-clans", {timeout: 5000}).then(r => setAllClans(r.data)).catch(() => {}); }}
          onInvite={() => setShowInvite(true)}
          onLeave={leave}
          onDelete={delClan}
          onArchive={archive}
          leaving={leaving}
        />
      </div>

      {/* ── Logo settings (staff) ── */}
      {flags.isStaff && (
        <section className="bg-[#111113]/90 border border-white/10 rounded-2xl p-5 shadow-xl" data-testid="clan-logo-settings">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={18} className="text-gold-500" />
            <h2 className="font-display font-black text-lg text-white">تخصيص شعار الكلان</h2>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <ClanLogo
              clan={clan}
              className="h-16 w-16 rounded-xl border border-white/15 bg-black/40 overflow-hidden grid place-items-center shrink-0"
              fallbackIconSize={24}
              fallbackIconClassName="text-white/30"
            />

            <div className="flex-1 min-w-[220px]">
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 cursor-pointer text-xs shadow-md transition">
                <ImageIcon size={15} /> {logoUploading ? "جاري الرفع..." : "رفع شعار الكلان"}
                <input
                  type="file"
                  accept=".gif,.png,.jpg,.jpeg,image/gif,image/png,image/jpeg"
                  onChange={onUploadClanLogo}
                  className="hidden"
                  disabled={logoUploading}
                />
              </label>
              <div className="mt-1.5 text-[11px] text-white/40">
                المسموح: PNG, JPG{(clan.isPlusClan || clan.is_plus || (clan.max_members || 0) >= 9) ? ", GIF" : ""}
              </div>
              {!(clan.isPlusClan || clan.is_plus || (clan.max_members || 0) >= 9) && (
                <div className="mt-1 text-[11px] text-amber-400 font-bold">ميزة الشعار المتحرك GIF حصرية لكلانات البلس!</div>
              )}
            </div>
          </div>
 
        </section>
      )}

      {/* ── Instant challenge bar ── */}
      {isExternalLeader && (
        <div className="rounded-2xl border border-white/10 bg-[#111113]/90 p-5 flex items-center justify-between gap-4 flex-wrap shadow-xl">
          <div className="flex items-center gap-3">
            <Swords size={20} className={canInstantChallenge ? "text-destructive" : "text-white/30"} />
            <p className="text-xs md:text-sm text-white/70 font-medium">
              {!ownAttendanceReady ? ownChallenge.reason : !targetIsGreen ? `نقص محضّري الخصم: ${attendance?.summary?.count ?? clan?.attendance?.count ?? 0}/6` : "يمكن للقائد أو النائب المحضّر إرسال التحدي؛ الطرفان لديهما 6 محضّرين على الأقل."}
            </p>
          </div>
          <button
            onClick={instantChallenge}
            disabled={!canInstantChallenge}
            className="px-5 py-2.5 rounded-xl bg-destructive text-white font-black hover:bg-destructive/90 disabled:opacity-40 disabled:cursor-not-allowed text-xs shadow-md transition"
          >
            تحدي الكلان الآن ⚔️
          </button>
        </div>
      )}

      {/* ── Attendance + Top MVP ── */}
      <div className="grid lg:grid-cols-2 gap-6">
        <AttendancePanel
          clan={clan}
          attendance={attendance}
          canInteract={!!flags.isMember}
          canManage={!!flags.isStaff}
          isCheckedIn={isCheckedIn}
          highlightCheckIn={highlightCheckIn}
          onCheckIn={checkIn}
          onCheckOut={checkOut}
          onRemove={removeAttendance}
        />
        <TopMvpWidget rows={mvpRows} />
      </div>

      {/* ── Trophy room ── */}
      <TrophyRoom trophies={clan.trophies || []} />

      {/* ── Available leagues ── */}
      {flags.isStaff && (
        <LeaguesJoin
 
        clanLeagueIds={clan.league_ids || []}
          leagues={leagues}
          reload={load}
          clanMembersCount={(clan.member_ids || []).length}
        />
      )}

      {/* ── Roster footer ── */}
      <section className="bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 bg-white/[0.02]">
          <h2 className="font-display font-black text-lg text-white flex items-center gap-2">
            <Shield size={18} className="text-white/40" />
            أعضاء الكلان ({clan.members?.length || 0}/{clan.max_members || 6})
          </h2>
          <span className="text-xs text-white/30 font-mono">Roster</span>
        </div>
        <div className="p-5">
          <MembersList
            members={clan.members || []}
            leaderId={clan.leader_id}
            viceLeaderIds={clan.vice_leader_ids || []}
            canManage={flags.isLeader || user?.role === "admin"}
            onPromote={promote}
            onKick={kick}
          />
          {flags.isLeader && (
            <div className="mt-4 text-xs text-white/40 font-medium">
              النواب الحاليين: {clan.vice_leader_ids?.length || 0} / {clan.max_vices || 2}
              {clan.max_vices === 0 && (
                <Link to="/me" className="text-gold-500 mr-2 hover:underline font-bold">ترقية الكلان إلى Plus لإضافة نواب</Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Join requests ── */}
      {flags.isStaff && <RequestsList requests={requests} onAccept={accept} onReject={reject} />}

      {/* ── Pending challenges ── */}
      {flags.isStaff && (
        <ChallengesList
          challenges={challenges}
          currentClanId={clan.id}
          onAccept={(cid) => respondChallenge(cid, "accept")}
          onReject={(cid) => respondChallenge(cid, "reject")}
        />
      )}

      {/* 📝 مودال تعديل بيانات الكلان */}
      {showEditClanModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200" dir="rtl">
          <form onSubmit={saveClanDetails} className="bg-[#141417] border border-white/15 rounded-3xl p-6 md:p-8 w-full max-w-md space-y-4 shadow-2xl relative">
            <button type="button" onClick={() => setShowEditClanModal(false)} className="absolute top-5 left-5 text-white/40 hover:text-white transition p-1.5 rounded-lg hover:bg-white/5">
              <X size={18} />
            </button>

            <h2 className="font-display font-black text-xl text-white">تعديل بيانات الكلان 🛡️</h2>

            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">اسم الكلان *</label>
              <input
                required minLength={2} maxLength={40}
                value={editClanForm.name}
                onChange={(e) => setEditClanForm({ ...editClanForm, name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 text-sm text-white transition"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">اختصار الكلان (Tag) *</label>
              <input
                required minLength={2} maxLength={8}
                value={editClanForm.tag}
                onChange={(e) => setEditClanForm({ ...editClanForm, tag: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 text-sm text-white font-mono uppercase transition"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-white/70 mb-1 block">وصف الكلان</label>
              <textarea
                value={editClanForm.description}
                onChange={(e) => setEditClanForm({ ...editClanForm, description: e.target.value })}
                rows={3}
                placeholder="اكتب وصفاً مختصراً عن الكلان..."
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 resize-none text-sm text-white transition"
              />
            </div>

            <div className="flex gap-2.5 justify-end pt-2">
              <button type="button" onClick={() => setShowEditClanModal(false)} className="px-4 py-2.5 rounded-xl text-xs text-white/70 font-bold hover:bg-white/5 transition">
                إلغاء
              </button>
              <button type="submit" disabled={editClanBusy} className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-md transition disabled:opacity-50">
                {editClanBusy ? "جاري الحفظ..." : "حفظ التغييرات"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modals ── */}
      {showChallenge && (
        <ChallengeModal
          clan={clan}
          allClans={availableOpponents}
          canSend={ownAttendanceReady}
          readinessReason={ownChallenge.reason}
          opponent={opponent}
          onOpponentChange={setOpponent}
          onClose={() => setShowChallenge(false)}
          onSubmit={challenge}
        />
      )}

      {showInvite && (
        <InviteModal
          search={inviteSearch}
          results={inviteResults}
          onSearch={searchInvite}
          onInvite={sendInvite}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}
